import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import useSWR, { useSWRConfig } from 'swr'

import { getEmailTemplate, getInvoiceById, getPlanLimits, sendInvoiceEmail } from '../../api'
import Spinner from '../../components/ui/spinner'
import Button, { ButtonSize, ButtonVariant } from '../../components/ui/button'
import Input from '../../components/ui/input'
import Checkbox from '../../components/ui/input/checkbox'
import { InvoiceStatus } from '../../types'
import { useAlerts } from '../../context/alerts'
import { useAuth } from '../../context/auth'
import TextArea from '../../components/ui/input/textArea'
import ClientForm from '../../components/clients/form'
import UpgradeToPremiumLink from '../../components/billing/UpgradeToPremiumLink'
import InvoiceOverlayModal from '../../components/modals'
import { parseClientToForm } from '../../lib/parsers'
import { AUTOMATIC_OVERDUE_FOLLOW_UPS_SECTION_ID } from '../../lib/profileSectionIds'
import { ReactComponent as EditIcon} from '../../static/images/edit.svg'
import './styles.scss'

type InvoicePdfPreviewProps = {
  url: string
}

function appendPreviewParam(url: string) {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}preview=1`
}

const InvoicePdfPreview = ({ url }: InvoicePdfPreviewProps) => {
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    setIsLoading(true)
  }, [url])

  return (
    <div className='send-email-page__preview-frame'>
      {isLoading && (
        <div className='send-email-page__preview-loading' aria-live='polite'>
          <Spinner />
          <span>Loading preview</span>
        </div>
      )}
      <iframe
        className='send-email-page__pdf'
        src={appendPreviewParam(url)}
        title='Invoice preview'
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </div>
  )
}

/**
 * Page to customise and confirm sending an email to a client.
 */
const SendEmailPage = () => {
  const navigate = useNavigate()
  const { addAlert } = useAlerts()
  const { user } = useAuth()
  const { mutate: mutateCache } = useSWRConfig()

  // get invoice
  const { id } = useParams()
  const { data: invoiceData, isLoading: isLoadingInvoice, mutate: mutateInvoice } = useSWR(id ? `/api/invoices/${id}`: null, getInvoiceById)
  const { data: planLimits, isLoading: isLoadingPlanLimits } = useSWR('/api/plan-limits', getPlanLimits)
  const { data: emailTemplate, isLoading: isLoadingEmailTemplate } = useSWR(id ? `/api/invoices/email?invoiceId=${id}` : null, getEmailTemplate)
  const { data: emailReminderTemplate, isLoading: isLoadingEmailReminderTemplate } = useSWR(id ? `/api/invoices/email-reminder?invoiceId=${id}` : null, getEmailTemplate)
  // get email template
  // send email trigger
  const { trigger: triggerSendEmail, isMutating: isSending } = useSWRMutation('/api/invoices/email', sendInvoiceEmail)
  const { trigger: triggerSendReminderEmail, isMutating: isSendingReminder } = useSWRMutation('/api/invoices/email-reminder', sendInvoiceEmail)
  const [values, setValues] = React.useState<{ emailContent: string, ccEmail: string }>({ emailContent: '', ccEmail: '' })
  const [showEditClientModal, setShowEditClientModal] = React.useState(false)
  const [showCcUpgradeModal, setShowCcUpgradeModal] = React.useState(false)
  const [ccEmailEnabled, setCcEmailEnabled] = React.useState(false)

  const canUseCcEmail = !!user?.hasActiveSubscription || !!planLimits?.freeAccess

  React.useEffect(() => {
    if (invoiceData?.ccEmail) {
      setValues((prev) => ({ ...prev, ccEmail: invoiceData.ccEmail || '' }))
      setCcEmailEnabled(true)
    }
  }, [invoiceData?.ccEmail])
  const handleBack = (e: any) => {
    e.preventDefault()
    navigate(-1)
  }

  const sendEmail = async (e: any) => {
    e.preventDefault()
    if(!triggerSendEmail || !triggerSendReminderEmail) throw new Error('triggerSendEmail is not defined')
    const overDueInvoice = invoiceData.status === InvoiceStatus.OVERDUE
    const trimmedCcEmail = canUseCcEmail && ccEmailEnabled ? values.ccEmail.trim() : ''
    const body = {
      invoiceId: invoiceData._id,
      content: values.emailContent,
      ...(trimmedCcEmail ? { ccEmail: trimmedCcEmail } : {}),
    }
    try {
      if(overDueInvoice) {
        await triggerSendReminderEmail({ body })
      } else {
        await triggerSendEmail({ body })
      }
      const invoiceCacheKey = `/api/invoices/${invoiceData._id}`
      await Promise.all([
        mutateCache(invoiceCacheKey),
        mutateCache(`${invoiceCacheKey}/history`),
        mutateCache('/api/invoices'),
      ])
      addAlert({ type: 'info', message: 'Email will be sent soon' })
      navigate(-1)
    } catch (error: any) {
      console.error(error)
      // redirectTo is handled by global SWR onError in App.tsx
      if (!error?.redirectTo) {
        addAlert({ type: 'error', message: error?.message || 'Error sending email. Please try later.' })
      }
    }
  }

  if(isLoadingInvoice || isLoadingPlanLimits || isLoadingEmailTemplate || isLoadingEmailReminderTemplate) return <Spinner />
  if (!invoiceData) {
    return <div className='col-lg-6'>Invoice not found.</div>
  }

  const handleChange = (e: any) => {
    e.preventDefault()
    const { name, value } = e.target
    setValues({ ...values, [name]: value })
  }

  const client = invoiceData.client
  const canSendToClient = Boolean(client?.email?.trim())
  const { defaultText: defaultNewInvoiceText, seeInvoiceUrl: seeNewInvoiceUrl } = emailTemplate?.data || {}
  const { defaultText: defaultReminderText, seeInvoiceUrl: seeReminderInvoiceUrl } = emailReminderTemplate?.data || {}

  const invoiceOverdue = invoiceData.status === InvoiceStatus.OVERDUE
  let defaultText = defaultNewInvoiceText
  let seeInvoiceUrl = seeNewInvoiceUrl
  if(invoiceOverdue) {
    defaultText = defaultReminderText
    seeInvoiceUrl = seeReminderInvoiceUrl
  }
  return (
    <div className='row g-4 g-lg-5 send-email-page'>
      <div className='col-12 col-lg-6 send-email-page__form'>
      <div>
        <Input label='Client Email' value={client?.email || ''} readOnly />
        <div className='mt-1'>
          <Button icon={<EditIcon width={14} />} disabled={!client?._id} iconPosition='start' variant={ButtonVariant.LIGHT} size={ButtonSize.SMALL} onClick={() => setShowEditClientModal(true)}>
            Edit Client
          </Button>
        </div>
        {!canSendToClient && (
          <small className='text-black d-block mt-2 bg-warning p-2 rounded'>
            There is no email on file for this client. Add one under Edit client, then return here to send.
          </small>
        )}
      </div>
      <div className='mt-3'>
        <Checkbox
          label='Add CC email'
          id='cc-email-toggle'
          checked={ccEmailEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!canUseCcEmail) {
              setShowCcUpgradeModal(true)
              return
            }
            const checked = e.target.checked
            setCcEmailEnabled(checked)
            if (!checked) {
              setValues((prev) => ({ ...prev, ccEmail: '' }))
            }
          }}
        />
        {canUseCcEmail && ccEmailEnabled && (
          <div className='mt-2'>
            <Input
              label='CC Email'
              type='email'
              name='ccEmail'
              value={values.ccEmail}
              onChange={handleChange}
              placeholder=''
            />
            <small className='text-body-secondary d-block mt-2'>
              Automatic overdue reminders won&apos;t be sent to this CC email.
            </small>
          </div>
        )}
      </div>
      <div className='mt-4 mb-4'>
        <div className='mt-2 card p-2 '>
          <TextArea label='Email Content' name='emailContent' className='form-control card-text no-border' rows={6} defaultValue={defaultText} onChange={handleChange} />
          <div className='mx-2 pb-5'><a href={seeInvoiceUrl} target='_blank' rel='noreferrer'>See Invoice</a></div>
        </div>
        {invoiceOverdue && (
          <Link
            to={`/profile#${AUTOMATIC_OVERDUE_FOLLOW_UPS_SECTION_ID}`}
            className='btn btn-link p-0 mt-2 d-inline-block'
          >
            Edit this template
          </Link>
        )}
      </div>
      <div className='d-flex justify-content-between'>
        <div>
          <Button size={ButtonSize.LARGE} variant={ButtonVariant.LIGHT} onClick={handleBack}>Back</Button>
        </div>
        <div>
          <Button
            size={ButtonSize.LARGE}
            onClick={sendEmail}
            disabled={isSending || isSendingReminder || !canSendToClient}
            loading={isSending || isSendingReminder}
            loadingText='Sending email'
            visibleLoadingText
          >
            Send
          </Button>
        </div>
      </div>
      <InvoiceOverlayModal
        open={showCcUpgradeModal}
        onClose={() => setShowCcUpgradeModal(false)}
        modalClassName='send-email-upgrade-modal'
      >
        <p className='send-email-upgrade-modal__message mb-0'>
          Upgrade to Premium to add a CC email
        </p>
        <div className='send-email-upgrade-modal__cta mt-3'>
          <UpgradeToPremiumLink />
        </div>
      </InvoiceOverlayModal>
      {showEditClientModal && client && (
        <div
          className='send-email-modal-backdrop'
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditClientModal(false)
            }
          }}
        >
          <div className='send-email-modal card border-3 p-4 bg-white'>
            <h3 className='h5 mb-3'>Edit client</h3>
            <ClientForm
              defaultValues={parseClientToForm(client)}
              onSave={async () => {
                await mutateInvoice()
                setShowEditClientModal(false)
              }}
              onCancel={() => setShowEditClientModal(false)}
              saveLabel='Update'
            />
          </div>
        </div>
      )}
      </div>
      <div className='col-lg-6 d-none d-lg-block send-email-page__preview'>
        <p className='send-email-page__preview-label'>Invoice Preview</p>
        {seeInvoiceUrl && <InvoicePdfPreview url={seeInvoiceUrl} />}
      </div>
    </div>
  )
}

export default SendEmailPage
